import { Component, computed, input, output } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { InputTextModule } from 'primeng/inputtext';
import { CodeMirrorEditorComponent } from '../new-code-mirror-editor/code-mirror-editor.component';
import { CombineOptionsValue } from '../../api/interfaces';

@Component({
  selector: 'lib-render-rules',
  template: `
    <div
      [formGroup]="form()"
      class="flex w-full flex-col gap-2 overflow-visible"
    >
      @if (showIfRule()) {
        <div class="flex w-full flex-col overflow-visible" id="showIf">
          <label htmlFor="showIf">Mostra se</label>
          <div class="relative flex flex-row items-center gap-2">
            <lib-code-mirror-editor
              [formControl]="showIfRule()"
            ></lib-code-mirror-editor>

            @if (showPickButton()) {
              <button
                mat-icon-button
                (click)="openShowIfSelection()"
                class="absolute right-1"
              >
                <mat-icon svgIcon="heroicons_outline:arrow-up-tray"></mat-icon>
              </button>
            }
          </div>
          @if (showIfRule().invalid) {
            @let error = showIfRule().errors?.['idNotExists'];
            <ul class="text-red-500">
              @if (error) {
                @for (id of error.value; track $index) {
                  <li>L'id '{{ id }}' non esiste nel modulo</li>
                }
              }
            </ul>
          }
        </div>
      }
      @let completionControl = autocompletionRule();
      @if (!hideAutocompletion() && completionControl) {
        <div class="flex w-full flex-col overflow-visible" id="completion">
          <label htmlFor="completion">Autocompila con</label>
          <div class="relative flex flex-row items-center gap-2">
            <lib-code-mirror-editor
              [formControl]="completionControl"
            ></lib-code-mirror-editor>

            @if (showPickButton()) {
              <button
                mat-icon-button
                (click)="openAutocompletionSelection()"
                class="absolute right-1"
              >
                <mat-icon svgIcon="heroicons_outline:arrow-up-tray"></mat-icon>
              </button>
            }
          </div>
          @if (completionControl.invalid) {
            @let error = completionControl.errors?.['idNotExists'];
            <ul class="text-red-500">
              @if (error) {
                @for (id of error.value; track $index) {
                  <li>L'id '{{ id }}' non esiste nel modulo</li>
                }
              }
            </ul>
          }
        </div>
      }
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    InputTextModule,
    CodeMirrorEditorComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RenderRulesComponent,
    },
  ],
})
export class RenderRulesComponent implements ControlValueAccessor {
  readonly form = input.required<UntypedFormGroup>();
  readonly showPickButton = input(true);
  readonly hideAutocompletion = input(true);

  readonly openedCompletion = output();
  readonly openedShowIf = output();

  showIfRule = computed(() => {
    return this.form().controls['showIfRule'] as FormControl<string>;
  });

  autocompletionRule = computed(() => {
    return this.form().controls['autocompletionRule'] as
      | FormControl<string>
      | undefined;
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  onTouched = (value: CombineOptionsValue) => {};

  writeValue(obj: CombineOptionsValue): void {
    if (!obj) return;

    this.form().setValue(obj, {
      emitEvent: false,
    });
  }

  registerOnChange(fn: (value: CombineOptionsValue) => void) {
    this.form().valueChanges.subscribe((value) =>
      fn(value as CombineOptionsValue),
    );
  }

  registerOnTouched(fn: (value: CombineOptionsValue) => void) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form().disable();
    } else {
      this.form().enable();
    }
  }

  openAutocompletionSelection() {
    this.openedCompletion.emit();
  }

  openShowIfSelection() {
    this.openedShowIf.emit();
  }
}
